/**
 * Legacy decorative card primitives. New code should reach for `Surface`
 * (default content panel) or `Section` (panel + header + body).
 *
 * `GlowCard` is kept for cases that genuinely need the gradient border
 * treatment — typically hero KPI tiles. `GlassSurface` is now a thin
 * compat shim over `Surface` so older detail pages render with the new
 * design tokens without needing a line-by-line rewrite.
 */
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { Surface } from '@/components/ui/surface';
import { cn } from '@/lib/cn';

export interface GlowCardProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  /** Adds a soft drop-shadow tinted with the primary color. */
  glow?: boolean;
  /** Disables hover lift. */
  flat?: boolean;
  /** Adds a subtle gradient border ring. */
  bordered?: boolean;
  children?: ReactNode;
}

export const GlowCard = forwardRef<HTMLDivElement, GlowCardProps>(
  ({ glow, flat, bordered, className, children, ...props }, ref) => {
    // When `glow` is set we keep the decorative gradient ring (hero KPI use).
    // Otherwise we render a plain `Surface` for visual consistency with the
    // new design system.
    if (!glow) {
      return (
        <motion.div
          ref={ref}
          whileHover={flat ? undefined : { y: -1 }}
          transition={{ type: 'spring', stiffness: 220, damping: 22 }}
          {...props}
          className={cn('rounded-xl', className)}
        >
          <Surface tone="default" className="h-full">
            {children}
          </Surface>
        </motion.div>
      );
    }

    // Glow variant — gradient ring + soft halo, used for KPI hero blocks.
    return (
      <motion.div
        ref={ref}
        whileHover={flat ? undefined : { y: -2 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
        className={cn(
          'group relative rounded-2xl shadow-glow',
          bordered !== false &&
            'bg-gradient-to-br from-primary/30 via-border to-violet-500/25 p-[1px]',
          className,
        )}
        {...props}
      >
        <div className="relative h-full w-full overflow-hidden rounded-[calc(theme(borderRadius.2xl)-1px)] bg-card text-card-foreground">
          <div className="relative">{children}</div>
        </div>
      </motion.div>
    );
  },
);
GlowCard.displayName = 'GlowCard';

/** Compat shim — alias for `Surface`. New code should import `Surface`. */
export const GlassSurface = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <Surface ref={ref} tone="default" className={className} {...props} />
  ),
);
GlassSurface.displayName = 'GlassSurface';
